import { Box, Button, Dialog, Drawer, Typography } from '@mui/material';
import { ReactElement } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactComponent as UploadSquareIcon } from 'assets/icons/uploadSquare.svg';
import {
  Backdrop,
  BaseTextFieldController,
  Form,
  SelectController,
  RichEditorController,
} from 'components';
import { DocumentCategoryIcons } from 'constants/DocumentCategoryIcons';
import { useWindowSize } from 'hooks';
import { richEditorStyles, theme } from 'styles';
import { CreateCategoryFormValues, DocumentCategory } from 'types';
import { required, isUntaggedCategory } from 'validations';

import { AllOption, MAX_ARTICLE_LENGTH } from './constants';
import { useCreateCategoryForm } from './hooks';
import { descriptionFieldRules, titleFieldRules } from './rules';
import { BottomSheetHandle } from './styled';
import { dropdownContainerSx, multiSelectStyles, optionSx } from './styles';

interface CreatePostFormProps {
  category?: DocumentCategory;
  isEdit: boolean;
  open?: boolean;
  onClose?: () => void;
}

export function CreateDocumentCategoriesForm({
  category,
  isEdit,
  open = false,
  onClose,
}: CreatePostFormProps): ReactElement {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();

  const {
    control,
    handleFormSubmit,
    handleAssigneeChange,
    isLoading,
    isNotDisableButton,
    usersOptions,
  } = useCreateCategoryForm({
    category,
    isEdit,
    onClose,
  });

  const selectedIcon = useWatch<CreateCategoryFormValues>({ control, name: 'icon' });

  const iconsOptions = Object.keys(DocumentCategoryIcons)
    .filter((name) => !isUntaggedCategory(name) && name !== 'newspaper' && name !== 'user')
    .map((name) => ({
      value: name,
      label: DocumentCategoryIcons[name],
    }));

  const formContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        px: 2,
        pb: 'env(safe-area-inset-bottom)',
      }}
    >
      <Typography variant="h6" fontWeight={700} fontSize="14px" mb={2}>
        {isEdit
          ? t('resources.documentCategories.create.editTitle')
          : t('resources.documentCategories.create.title')}
      </Typography>

      <Form onSubmit={handleFormSubmit}>
        <Typography variant="body2" color="text.secondary" fontSize="14px" mb={1}>
          {t('resources.documentCategories.create.icon')}
        </Typography>
        <Box>
          <Box
            sx={{
              position: 'relative',
              width: '44px',
              '& .MuiInputBase-root': {
                width: '44px !important',
                height: '44px !important',
              },
              '& .MuiSelect-select.MuiSelect-select': {
                width: '44px !important',
                height: '44px !important',
              },
            }}
          >
            {!selectedIcon && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '44px',
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                  borderRadius: '6px',
                  bgcolor: theme.palette.info.light,
                  zIndex: 1,
                }}
              >
                <UploadSquareIcon width={24} height={24} />
              </Box>
            )}
            <SelectController
              name="icon"
              control={control}
              placeholder=""
              options={iconsOptions}
              isIconSelect
              rules={required}
              optionSx={optionSx}
              dropdownContainerSx={dropdownContainerSx(false)}
            />
          </Box>
        </Box>

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" fontSize="14px" mb={1}>
            {t('resources.documentCategories.create.form.placeholders.title')}
          </Typography>
          <BaseTextFieldController
            name="title"
            control={control}
            placeholder={t('resources.documentCategories.create.form.placeholders.title')}
            sx={{
              '& .MuiInputBase-root': {
                borderRadius: '4px',
                background: '#fff',
                boxShadow: 'none',
                height: '44px',
                border: '1px solid #DDDDDD',
                '&:hover': { border: '1px solid #DDDDDD' },
                '&.Mui-focused': { border: '1px solid #DDDDDD' },
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
                height: '44px',
                padding: '0 12px',
                boxSizing: 'border-box',
              },
              '& .MuiInputBase-input::placeholder': { fontSize: '14px' },
              '.MuiFormHelperText-root.Mui-error': {
                position: 'static',
                top: 'auto',
                fontSize: '12px',
              },
            }}
            rules={titleFieldRules}
          />
        </Box>

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" fontSize="14px" mb={1}>
            {t('resources.documentCategories.create.form.placeholders.description')}
          </Typography>
          <RichEditorController
            name="description"
            control={control}
            placeholder={t('resources.documentCategories.create.form.placeholders.description')}
            maxLength={MAX_ARTICLE_LENGTH}
            rules={descriptionFieldRules}
            sx={richEditorStyles(isMobile)}
          />
        </Box>

        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" fontSize="14px" mb={1}>
            {t('resources.documentCategories.create.form.placeholders.visibility')}
          </Typography>
          <SelectController
            name="assigned_to"
            control={control}
            placeholder={t('resources.documentCategories.create.form.placeholders.visibility')}
            options={[AllOption, ...usersOptions]}
            multipleSelect
            sx={{
              ...multiSelectStyles(false),
              borderRadius: '4px',
              '.MuiOutlinedInput-notchedOutline': { border: 'none' },
            }}
            handleChange={handleAssigneeChange}
            type="poll"
            rules={required}
          />
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 500,
              height: '44px',
              px: 3,
              borderColor: '#E0E0E0',
              color: '#333',
              '&:hover': {
                borderColor: theme.palette.white.light,
                background: theme.palette.white.p100,
              },
            }}
          >
            {t('resources.documentCategories.create.form.buttons.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isEdit && !isNotDisableButton}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              height: '44px',
              px: 3,
              bgcolor: theme.palette.info.main,
              '&:hover': { bgcolor: theme.palette.info.light },
            }}
          >
            {isEdit
              ? t('resources.documentCategories.create.form.buttons.edit')
              : t('resources.documentCategories.create.form.buttons.create')}
          </Button>
        </Box>
      </Form>
    </Box>
  );

  return (
    <>
      {isLoading && <Backdrop />}
      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          disableEnforceFocus
          PaperProps={{
            sx: {
              borderRadius: '20px 20px 0 0',
              maxHeight: '90dvh',
              display: 'flex',
              flexDirection: 'column',
              pb: 'env(safe-area-inset-bottom)',
              paddingBottom: '16px',
            },
          }}
        >
          <BottomSheetHandle />
          {formContent}
        </Drawer>
      ) : (
        <Dialog
          open={open}
          onClose={onClose}
          disableEnforceFocus
          PaperProps={{
            sx: {
              borderRadius: '16px',
              padding: '16px',
              boxShadow: '0px 8px 32px rgba(0,0,0,0.12)',
              width: '100%',
              maxWidth: '560px',
            },
          }}
        >
          {formContent}
        </Dialog>
      )}
    </>
  );
}
